import React from 'react';
import { DrugAllergyItem, CodeSetItem } from '../../types/api';
import { DrugAllergyFormState } from '../../types/dispense';

interface DrugAllergyDetailModalProps {
  show: boolean;
  selectedDrug: DrugAllergyItem | null;
  isAddingNew: boolean;
  drugAllergyForm: DrugAllergyFormState;
  informationSourceList: CodeSetItem[];
  adverseReactionList: CodeSetItem[];
  drugAllergyIndicatorList: CodeSetItem[];
  drugProbabilityList: CodeSetItem[];
  alertIndicatorList: CodeSetItem[];
  reactionOutcomeList: CodeSetItem[];
  setShow: (show: boolean) => void;
  setSelectedDrug: (drug: DrugAllergyItem | null) => void;
  setEditingDrugAllergy: (allergy: any | null) => void;
  setIsAddingNewDrugAllergy: (value: boolean) => void;
  setDrugAllergyForm: (form: DrugAllergyFormState) => void;
  resetDrugAllergyForm: () => void;
  onSave: () => Promise<void>;
}

// Shared select style for form fields
const selectStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', border: '2px solid #d1d5db',
  borderRadius: '10px', fontSize: '15px', background: '#ffffff',
  cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit', color: '#1f2937'
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '10px', fontSize: '14px',
  fontWeight: 600, color: '#374151', letterSpacing: '0.3px'
};

const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
  e.currentTarget.style.borderColor = '#0284c7';
  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(2, 132, 199, 0.1)';
  e.currentTarget.style.background = '#f8fafc';
};

const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
  e.currentTarget.style.borderColor = '#d1d5db';
  e.currentTarget.style.boxShadow = 'none';
  e.currentTarget.style.background = '#ffffff';
};

export function DrugAllergyDetailModal({
  show, selectedDrug, isAddingNew, drugAllergyForm,
  informationSourceList, adverseReactionList, drugAllergyIndicatorList,
  drugProbabilityList, alertIndicatorList, reactionOutcomeList,
  setShow, setSelectedDrug, setEditingDrugAllergy, setIsAddingNewDrugAllergy,
  setDrugAllergyForm, resetDrugAllergyForm, onSave
}: DrugAllergyDetailModalProps) {
  if (!show || !selectedDrug) return null;

  const handleClose = () => {
    setShow(false);
    setSelectedDrug(null);
    setEditingDrugAllergy(null);
    setIsAddingNewDrugAllergy(false);
    resetDrugAllergyForm();
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', zIndex: 2500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="modal modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '700px', maxHeight: '90vh', background: '#ffffff', borderRadius: '20px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'slideUpModal 0.3s ease-out' }}
      >
        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: '2px solid #e5e7eb', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#0c4a6e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px', display: 'inline-block', transform: 'rotate(-5deg)' }}>💊</span>
              {isAddingNew ? 'Add Drug Allergy' : 'Edit Drug Allergy'}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b', marginBottom: '4px', lineHeight: '1.4' }}>
              {selectedDrug.description}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ background: '#e0f2fe', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>Code: {selectedDrug.code}</span>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'rgba(255, 255, 255, 0.8)', border: '2px solid rgba(0, 0, 0, 0.1)', borderRadius: '12px', width: '44px', height: '44px', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 300, transition: 'all 0.2s ease', flexShrink: 0, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'; e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.transform = 'scale(1)'; }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* Form Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px', background: '#fafbfc' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '24px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <span style={{ fontSize: '22px' }}>📋</span>
              Allergy Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {/* Allergy Report Source */}
              <div>
                <label style={labelStyle}>Allergy Report Source <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={drugAllergyForm.informationSourceCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, informationSourceCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  {informationSourceList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>

              {/* Reaction Description */}
              <div>
                <label style={labelStyle}>Reaction Description</label>
                <select value={drugAllergyForm.reactionCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, reactionCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  <option value="0">-- Select Reaction --</option>
                  {adverseReactionList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>

              {/* Drug Allergy Confirmation */}
              <div>
                <label style={labelStyle}>Drug Allergy Confirmation <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={drugAllergyForm.drugAllergyIndicatorCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, drugAllergyIndicatorCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  {drugAllergyIndicatorList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>

              {/* Drug-Allergy Probability */}
              <div>
                <label style={labelStyle}>Drug-Allergy Probability <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={drugAllergyForm.drugProbabilityCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, drugProbabilityCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  {drugProbabilityList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>

              {/* Avoidance Guide */}
              <div>
                <label style={labelStyle}>Avoidance Guide <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={drugAllergyForm.alertIndicatorCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, alertIndicatorCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  {alertIndicatorList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>

              {/* Severity */}
              <div>
                <label style={labelStyle}>Severity <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={drugAllergyForm.outcomeCode} onChange={(e) => setDrugAllergyForm({ ...drugAllergyForm, outcomeCode: e.target.value })} style={selectStyle} onFocus={handleFocus} onBlur={handleBlur}>
                  {reactionOutcomeList.map(item => <option key={item.code} value={item.code}>{item.description}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ padding: '20px 28px', borderTop: '2px solid #e5e7eb', background: '#ffffff', display: 'flex', gap: '12px', justifyContent: 'flex-end', flexShrink: 0, boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            style={{ padding: '14px 28px', fontSize: '15px', fontWeight: 600, borderRadius: '12px', transition: 'all 0.2s ease', border: '2px solid #d1d5db', background: '#ffffff', color: '#374151', cursor: 'pointer', minWidth: '120px' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            style={{ padding: '14px 32px', fontSize: '16px', fontWeight: 700, borderRadius: '12px', background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#ffffff', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease', minWidth: '160px', boxShadow: '0 4px 6px rgba(2, 132, 199, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #0369a1 0%, #075985 100%)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(2, 132, 199, 0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(2, 132, 199, 0.3)'; }}
          >
            <span style={{ fontSize: '18px' }}>💾</span>
            {isAddingNew ? 'Save Drug Allergy' : 'Update Drug Allergy'}
          </button>
        </div>
      </div>
    </div>
  );
}
